import React, { useRef } from 'react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import './PayslipScreen.css';
import dayjs from 'dayjs';

// Helper to format date as dd-mm-yyyy
function formatDateDMY(date) {
  if (!date) return '';
  const d = dayjs(date);
  if (!d.isValid()) return date;
  return d.format('DD-MM-YYYY');
}

const PayslipScreen = (props) => {
  const payslip = props.payslip;
  const payslipRef = useRef();

  const downloadPdf = async () => {
    try {
      // Before rendering to canvas, temporarily update all date fields to dd-mm-yyyy
      const input = payslipRef.current;
      if (!input) return;

      // Find and update all date fields in the DOM for PDF snapshot
      const dateNodes = input.querySelectorAll('[data-date]');
      const originalDates = [];
      dateNodes.forEach(node => {
        originalDates.push(node.textContent);
        node.textContent = formatDateDMY(node.textContent);
      });

      const canvas = await html2canvas(input, {
        scale: 2,
        useCORS: true,
      });

      // Restore original date text
      dateNodes.forEach((node, i) => {
        node.textContent = originalDates[i];
      });

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = pdfWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }
      pdf.save(`Payslip_${payslip?.staffName || 'employee'}.pdf`);
    } catch (err) {
      console.error('PDF generation failed:', err);
    }
  };

  if (!payslip) {
    return <p>Loading payslip...</p>;
  }

  return (
    <div className="payslip-screen">
      <div
        style={{
          display: 'flex',
          gap: 8,
          marginBottom: 16,
        }}
      >
        <button onClick={downloadPdf}>
          Download PDF
        </button>
      </div>

      <div className="payslip-container" ref={payslipRef}>
        {/* Company Info */}
        <section className="section">
          <div>
            <h3>Company Info</h3>
            <p>{payslip.companyName}</p>
            <p>{payslip.companyAddress}</p>
            <p>
              {payslip.companyEmail} | {payslip.companyPhone}
            </p>
          </div>

          <div>
            <h3>Payslip Info</h3>

            <p>
              <strong>Date:</strong> <span data-date>{formatDateDMY(payslip.date)}</span>
            </p>

            <p>
              <strong>Pay Period:</strong>{' '}
              <span data-date>{formatDateDMY(payslip.dateFrom)}</span> - <span data-date>{formatDateDMY(payslip.dateTo)}</span>
            </p>
          </div>
        </section>

        {/* Employee Info */}
        <section className="section">
          <h3>Employee Info</h3>

          <p>
            <strong>Name:</strong> {payslip.staffName}
          </p>

          <p>
            <strong>TFN:</strong> {payslip.staffTfn}
          </p>

          <p>
            <strong>Email:</strong> {payslip.staffEmail}
          </p>
        </section>

        {/* Pay Breakdown */}
        <section className="section">
          <h3>Pay Breakdown</h3>

          <table>
            <thead>
              <tr>
                <th>Type</th>
                <th>Hours</th>
                <th>Rate</th>
                <th>Total</th>
              </tr>
            </thead>

            <tbody>
              <tr>
                <td>Normal Hours</td>
                <td>{payslip.normalHours || 0}</td>
                <td>${payslip.basePayRate || 0}</td>
                <td>${payslip.payForNormalHours || 0}</td>
              </tr>

              {Number(payslip.overtimeRate1Hours) > 0 && (
                <tr>
                  <td>Overtime 1</td>
                  <td>{payslip.overtimeRate1Hours}</td>
                  <td>${payslip.overtimeRate1PayRate}</td>
                  <td>${payslip.payForOvertimeRate1Hours}</td>
                </tr>
              )}

              {Number(payslip.overtimeRate2Hours) > 0 && (
                <tr>
                  <td>Overtime 2</td>
                  <td>{payslip.overtimeRate2Hours}</td>
                  <td>${payslip.overtimeRate2PayRate}</td>
                  <td>${payslip.payForOvertimeRate2Hours}</td>
                </tr>
              )}

              {Number(payslip.eveningHours) > 0 && (
                <tr>
                  <td>Evening Hours</td>
                  <td>{payslip.eveningHours}</td>
                  <td>${payslip.eveningPayRate}</td>
                  <td>${payslip.payForEveningHours}</td>
                </tr>
              )}

              {Number(payslip.saturdayHours) > 0 && (
                <tr>
                  <td>Saturday Hours</td>
                  <td>{payslip.saturdayHours}</td>
                  <td>${payslip.saturdayPayRate}</td>
                  <td>${payslip.payForSaturdayHours}</td>
                </tr>
              )}

              {Number(payslip.sundayHours) > 0 && (
                <tr>
                  <td>Sunday Hours</td>
                  <td>{payslip.sundayHours}</td>
                  <td>${payslip.sundayPayRate}</td>
                  <td>${payslip.payForSundayHours}</td>
                </tr>
              )}
            </tbody>
          </table>
        </section>

        {/* Summary */}
        <section className="section">
          <h3>Summary</h3>

          <p>
            <strong>Gross Pay:</strong> ${payslip.totalPay || 0}
          </p>

          <p>
            <strong>Tax Withheld:</strong> ${payslip.taxWithheld || 0}
          </p>

          <p>
            <strong>Net Pay:</strong> ${payslip.netPay || 0}
          </p>

          <p>
            <strong>Superannuation:</strong> $
            {payslip.superannuation || 0}
          </p>

          <p>
            <strong>Total Hours:</strong>{' '}
            {payslip.totalHoursWorked || 0}
          </p>
        </section>

        {/* YTD */}
        <section className="section">
          <h3>Year-to-Date (YTD)</h3>

          <p>
            <strong>Gross YTD:</strong> $
            {payslip.grossPayYtd || 0}
          </p>

          <p>
            <strong>Tax YTD:</strong> $
            {payslip.taxWithheldYtd || 0}
          </p>

          <p>
            <strong>Net Pay YTD:</strong> $
            {payslip.netPayYtd || 0}
          </p>

          <p>
            <strong>Super YTD:</strong> $
            {payslip.superYtd || 0}
          </p>
        </section>

        {/* Leave Info */}
        <section className="section">
          <h3>Leave Info</h3>

          <p>
            <strong>Annual Leave Accrued:</strong>{' '}
            {payslip.annualLeaveAccruedHours || 0} hrs
          </p>

          <p>
            <strong>Annual Leave Taken:</strong>{' '}
            {payslip.annualLeaveTakenHours || 0} hrs
          </p>

          <p>
            <strong>Annual Leave Balance:</strong>{' '}
            {payslip.annualLeaveBalanceHours || 0} hrs
          </p>

          <p>
            <strong>Personal Leave Accrued:</strong>{' '}
            {payslip.personalLeaveAccruedHours || 0} hrs
          </p>

          <p>
            <strong>Personal Leave Taken:</strong>{' '}
            {payslip.personalLeaveTakenHours || 0} hrs
          </p>

          <p>
            <strong>Personal Leave Balance:</strong>{' '}
            {payslip.personalLeaveBalanceHours || 0} hrs
          </p>

          <p>
            <strong>Long Service Leave Accrued:</strong>{' '}
            {payslip.longServiceLeaveAccruedHours || 0} hrs
          </p>

          <p>
            <strong>Long Service Leave Taken:</strong>{' '}
            {payslip.longServiceLeaveTakenHours || 0} hrs
          </p>

          <p>
            <strong>Long Service Leave Balance:</strong>{' '}
            {payslip.longServiceLeaveBalanceHours || 0} hrs
          </p>
        </section>
      </div>
    </div>
  );
};

export default PayslipScreen;